/**
 * Selecting loose legs and naming them — ROADMAP 9.26.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SELECTION IS PRUNED AGAINST THE BOOK, EVERY RENDER
 *
 * A leg can leave the manual block while it is ticked: the trader exits it,
 * it is dragged into a basket, another tab groups it, or the poller finds
 * the broker no longer reports it. A `Set` of ids that nobody prunes would
 * keep that leg selected invisibly, and the GROUP button would count and
 * then send an id the server has to refuse.
 *
 * So the live set is always the intersection with what is on screen. The
 * stored set is the trader's intent; this is what survives of it.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useMemo, useState } from 'react'
import type { OpenBookTrade } from './useOpenBook'

/**
 * A leg may be grouped while it HOLDS something — `legTransferPolicy`'s rule.
 *
 * This has always read `netQty !== 0 && status === 'FILLED'` while its
 * comment claimed to mirror a stricter server rule that also demanded the
 * leg be wholly held. The two genuinely disagreed: the chain offered a
 * part-closed leg, the trader ticked it, and the drop came back "Only a leg
 * that is wholly held can be moved". 9.50 settled it on this side's answer —
 * see that rule for why realised P&L travelling with the leg is the
 * intended behaviour and not a defect.
 */
const selectable = (t: OpenBookTrade): boolean =>
  t.legs.some((l) => l.netQty !== 0 && l.status === 'FILLED')

export interface LegGrouping {
  /** Ticked AND still on screen. */
  selected: ReadonlySet<string>
  toggle: (tradeLegId: string) => void
  clear: () => void
  /** Two is the floor: one named leg is a card drawn around a row. */
  canGroup: boolean
  /**
   * Said before the group is made, not discovered after — ROADMAP 9.26.
   *
   * `heldPayoff` refuses a trade whose legs expire on different days: a
   * single expiry curve would price the far leg as though its time value
   * were already gone. The basket is then drawn RISK NOT KNOWN, and
   * because an unpriceable basket makes the portfolio total unknown too,
   * one calendar group takes the open-risk line down with it.
   *
   * That is the correct answer and a surprising one to meet afterwards, so
   * the dialog says it while the trader can still change their mind.
   */
  warning: string | null
}

export function useLegGrouping(manual: readonly OpenBookTrade[]): LegGrouping {
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set())

  const onScreen = useMemo(() => {
    const ids = new Set<string>()
    for (const t of manual) {
      if (!selectable(t)) continue
      for (const l of t.legs) if (l.netQty !== 0 && l.status === 'FILLED') ids.add(l.tradeLegId)
    }
    return ids
  }, [manual])

  const selected = useMemo(() => {
    const live = new Set<string>()
    for (const id of ticked) if (onScreen.has(id)) live.add(id)
    return live
  }, [ticked, onScreen])

  const toggle = useCallback((tradeLegId: string) => {
    setTicked((prev) => {
      const next = new Set(prev)
      if (!next.delete(tradeLegId)) next.add(tradeLegId)
      return next
    })
  }, [])

  const clear = useCallback(() => setTicked(new Set()), [])

  const warning = useMemo(() => {
    if (selected.size < 2) return null

    const expiries = new Set<string>()
    const accounts = new Set<string>()
    const underlyings = new Set<string>()
    for (const t of manual) {
      for (const l of t.legs) {
        if (!selected.has(l.tradeLegId)) continue
        expiries.add(l.expiry.slice(0, 10))
        accounts.add(t.broker.connectionId)
        underlyings.add(t.underlying)
      }
    }

    /*
     * ── WHAT THE SERVER WILL REFUSE, SAID BEFORE THE PRESS ────────────────
     *
     * `planLegTransfer` refuses a leg crossing a broker account or an
     * underlying, and `groupLegs` reports those per leg rather than failing
     * the whole action. Correct, and a poor way to find out: the trader
     * presses GROUP, the basket forms without the leg they wanted most, and
     * the sentence explaining it is in a dialog they are already closing.
     *
     * These two come FIRST because they are refusals. The expiry note below
     * is only a consequence.
     */
    if (accounts.size > 1) {
      return `These legs sit in ${accounts.size} different broker accounts. A basket is one account, so only the legs from the first one will be grouped.`
    }
    if (underlyings.size > 1) {
      return `These legs are on ${underlyings.size} different underlyings. A basket trades one, so only the legs matching the first will be grouped.`
    }

    if (expiries.size <= 1) return null

    /*
     * 9.28. A calendar is readable when every later-dated leg is LONG —
     * valuing them at intrinsic overstates the loss, so the figure is a
     * safe ceiling. A SHORT later leg is the one that cannot be priced,
     * because intrinsic would understate what it costs to close.
     */
    const near = [...expiries].sort()[0]!
    let shortLater = 0
    for (const t of manual) {
      for (const l of t.legs) {
        if (!selected.has(l.tradeLegId)) continue
        if (l.expiry.slice(0, 10) > near && l.netQty < 0) shortLater += 1
      }
    }

    if (shortLater > 0) {
      return `These legs expire on ${expiries.size} different dates and ${shortLater} of the later ones ${shortLater === 1 ? 'is' : 'are'} short. Open risk cannot be measured for that shape, so this basket — and the portfolio total — will read RISK NOT KNOWN.`
    }
    return `These legs expire on ${expiries.size} different dates, so open risk will be shown as a ceiling (≤) rather than an exact figure.`
  }, [manual, selected])

  return { selected, toggle, clear, canGroup: selected.size >= 2, warning }
}
